import { logger } from 'src/helpers/logger';
import type { IBlockedRemoval, INamedRef, ParsedErrorMap } from '../sharedTypes';

type NestedErrors = { [key: string]: unknown };

const isPlainObject = (value: unknown): value is NestedErrors =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const segmentFor = (key: string, parentKey: string | null): string =>
  /^\d+$/.test(key) && parentKey !== null ? `[${key}]` : key;

const parseErrors = (errors: unknown): ParsedErrorMap => {
  const result: ParsedErrorMap = {};

  const visit = (node: NestedErrors, path: string, lastKey: string | null) => {
    Object.entries(node).forEach(([key, value]) => {
      const segment = segmentFor(key, lastKey);
      const nextPath = path === '' ? segment : `${path}${segment.startsWith('[') ? '' : '.'}${segment}`;

      if (Array.isArray(value)) {
        const messages = value.filter((message): message is string => typeof message === 'string');

        // `blocked_removals` sits among the message arrays but holds objects; it is read separately
        // and must not leave an empty entry behind here.
        if (messages.length) result[nextPath] = messages;

        return;
      }

      if (isPlainObject(value)) visit(value, nextPath, key);
    });
  };

  if (isPlainObject(errors)) visit(errors, '', null);

  const hasEntries = Array.isArray(errors)
    ? errors.length > 0
    : isPlainObject(errors) && Object.keys(errors).length > 0;

  if (hasEntries && !Object.keys(result).length) {
    logger.error(`[agencyContractsV2] no messages parsed from validations: ${JSON.stringify(errors)}`);
  }

  return result;
};

const isNamedRef = (value: unknown): value is INamedRef =>
  isPlainObject(value) && typeof value.id === 'string' && typeof value.name === 'string';

// Every field the panel renders is checked: a reshaped row would otherwise reach the table and throw on
// `unit.name`, or print a blank count and date.
const isBlockedRemoval = (row: unknown): row is IBlockedRemoval =>
  isPlainObject(row) &&
  isNamedRef(row.unit) &&
  isNamedRef(row.position) &&
  typeof row.booking_count === 'number' &&
  typeof row.earliest_starts_at === 'string';

export const extractBlockedRemovals = (validations: unknown): IBlockedRemoval[] => {
  const rows = isPlainObject(validations) ? validations.blocked_removals : null;

  if (!Array.isArray(rows)) return [];

  const blocked = rows.filter(isBlockedRemoval);

  if (blocked.length !== rows.length) {
    logger.error(`[agencyContractsV2] blocked removals carried unreadable rows: ${JSON.stringify(rows)}`);
  }

  return blocked;
};

// `unitIdByIndex` must be the order the payload was built in: the 422 indexes the array it was sent. An index
// that names no unit is handed back under its path, so the refusal survives even when no row can hold it.
export const unitErrorsByUnitId = (
  parsed: ParsedErrorMap,
  unitIdByIndex: string[],
): { byUnit: Record<string, string[]>; unresolved: ParsedErrorMap } => {
  const byUnit: Record<string, string[]> = {};
  const unresolved: ParsedErrorMap = {};

  Object.entries(parsed).forEach(([path, messages]) => {
    const match = /^units\[(\d+)\]/.exec(path);

    if (!match) return;

    const unitId = unitIdByIndex[Number(match[1])];

    if (unitId) byUnit[unitId] = [...(byUnit[unitId] ?? []), ...messages];
    else unresolved[path] = messages;
  });

  return { byUnit, unresolved };
};

export default parseErrors;
